import React, { useEffect, useRef, useState } from 'react';
import { Tab, TabsContainer } from './HorizontalTabs.style';
import { useTranslation } from 'react-i18next';
import { isUK, isUkraine } from 'utils/countryCode';

const DEFAULT_HEADER_HEIGHT = 75;

export type HorizontalTabs =
  | 'RecommendedProducts'
  | 'SeedDensityDemandAndAdvice'
  | 'ProductComparison'
  | 'ProductPerformance'
  | 'AgronomicPreferences'
  | 'EnvironmentalStress'
  | 'TrialingInformation'
  | 'ManagementAndPlantingAdvice'
  | 'HerbicideTechnology';

interface HorizontalTabssProps {
  selectedTab: HorizontalTabs;
  handleTabClick: (tab: HorizontalTabs) => void;
  isDDG?: boolean;
  countryCode?: string;
  isMobile?: boolean;
  crop?: string;
  sectionOrder?: string[];
}

const HorizontalTabss: React.FC<HorizontalTabssProps> = ({
  selectedTab,
  handleTabClick,
  isDDG,
  countryCode,
  isMobile,
  crop,
  sectionOrder,
}) => {
  const isTabsSticky = !isDDG || isUkraine(countryCode) || isUK(countryCode);
  const [t] = useTranslation();
  const [topPosition, setTopPosition] = useState(DEFAULT_HEADER_HEIGHT);
  const observerRef = useRef<MutationObserver | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const bannerFoundRef = useRef<boolean>(false);

  const isBrazil = countryCode?.toLowerCase() === 'br';
  const isSunflower = crop?.toLowerCase() === 'sunflower';

  useEffect(() => {
    const calculateTopPosition = () => {
      const banner = document.querySelector('[data-banner="osr-edit"]') as HTMLElement | null;
      const header = document.querySelector('header') as HTMLElement | null;
      const measurableHeaderHeight = header?.offsetHeight ?? DEFAULT_HEADER_HEIGHT;
      const fallbackHeaderHeight = banner ? measurableHeaderHeight : DEFAULT_HEADER_HEIGHT;

      if (banner) {
        const calculatedTop = measurableHeaderHeight + banner.offsetHeight;
        setTopPosition(calculatedTop);
        bannerFoundRef.current = true;

        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }

        if (!resizeObserverRef.current) {
          resizeObserverRef.current = new ResizeObserver(() => {
            const updatedHeader = document.querySelector('header') as HTMLElement | null;
            const updatedBanner = document.querySelector(
              '[data-banner="osr-edit"]'
            ) as HTMLElement | null;
            const updatedHeaderHeight = updatedHeader?.offsetHeight ?? DEFAULT_HEADER_HEIGHT;
            const updatedBannerHeight = updatedBanner?.offsetHeight ?? 0;
            setTopPosition(updatedHeaderHeight + updatedBannerHeight);
          });
          resizeObserverRef.current.observe(banner);
          if (header) {
            resizeObserverRef.current.observe(header);
          }
        }

        return true;
      }

      setTopPosition(fallbackHeaderHeight);
      return false;
    };

    const bannerFound = calculateTopPosition();

    if (!bannerFound) {
      const contentContainer = document.querySelector('main') || document.body;

      observerRef.current = new MutationObserver(() => {
        if (!bannerFoundRef.current) {
          const banner = document.querySelector('[data-banner="osr-edit"]');
          if (banner) {
            calculateTopPosition();
          }
        }
      });

      observerRef.current.observe(contentContainer, {
        childList: true,
        subtree: true,
      });

      const timeoutId1 = setTimeout(() => {
        if (!bannerFoundRef.current) {
          calculateTopPosition();
        }
      }, 100);

      const timeoutId2 = setTimeout(() => {
        if (!bannerFoundRef.current) {
          calculateTopPosition();
        }
      }, 500);

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
      };
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  // Mapping from sectionOrder keys to tab keys
  const sectionToTabMap: Record<string, HorizontalTabs> = {
    'seed-density': 'SeedDensityDemandAndAdvice',
    'expert-seed-advice': 'RecommendedProducts',
    'product-comparison': 'ProductComparison',
    'product-performance': 'ProductPerformance',
    'agronomic-preferences': 'AgronomicPreferences',
    'environmental-stress': 'EnvironmentalStress',
    'trial-information': 'TrialingInformation',
    'growth-stages': 'ManagementAndPlantingAdvice',
    'herbicide-technology': 'HerbicideTechnology',
  };

  const allTabs: Array<{ key: HorizontalTabs; label: string; testId: string }> = [
    {
      key: 'RecommendedProducts',
      label: t('Recommended Products'),
      testId: 'recommended-products-tab',
    },
    {
      key: 'ProductComparison',
      label: t('Product Comparison'),
      testId: 'product-comparison-tab',
    },
    {
      key: 'ProductPerformance',
      label: t('Product Performance'),
      testId: 'product-performance-tab',
    },
    {
      key: 'AgronomicPreferences',
      label: t('Agronomic Preferences'),
      testId: 'agronomic-preferences-tab',
    },
    {
      key: 'EnvironmentalStress',
      label: t('Environmental Stress'),
      testId: 'environmental-stress-tab',
    },
    {
      key: 'TrialingInformation',
      label: t('Trialing Information'),
      testId: 'trialing-information-tab',
    },
    {
      key: 'ManagementAndPlantingAdvice',
      label: t('Management and Planting Advice'),
      testId: 'management-planting-advice-tab',
    },
    {
      key: 'HerbicideTechnology',
      label: t('Herbicide Technology'),
      testId: 'herbicide-technology-tab',
    },
    {
      key: 'SeedDensityDemandAndAdvice',
      label: t('Seed Density'),
      testId: 'seed-density-advice-tab',
    },
  ];

  // Filter tabs based on conditions
  let filteredTabs = allTabs.filter((tab) => {
    // Hide ProductComparison, ProductPerformance, and EnvironmentalStress for Brazil
    if (
      isBrazil &&
      ['ProductComparison', 'ProductPerformance', 'EnvironmentalStress'].includes(tab.key)
    ) {
      return false;
    }

    // Show HerbicideTechnology only for sunflower
    if (tab.key === 'HerbicideTechnology' && !isSunflower) {
      return false;
    }

    return true;
  });

  // Sort tabs according to sectionOrder if provided
  if (sectionOrder && sectionOrder.length > 0) {
    const tabsMap = new Map(filteredTabs.map((tab) => [tab.key, tab]));
    const orderedTabs: typeof filteredTabs = [];

    // Skip first 4 items for Variant-D and process the rest
    const relevantSections = sectionOrder.slice(4);

    relevantSections.forEach((section) => {
      const tabKey = sectionToTabMap[section];
      if (tabKey && tabsMap.has(tabKey)) {
        orderedTabs.push(tabsMap.get(tabKey)!);
        tabsMap.delete(tabKey);
      }
    });

    tabsMap.forEach((tab) => {
      orderedTabs.push(tab);
    });

    filteredTabs = orderedTabs;
  }

  return (
    <TabsContainer
      isTabsSticky={isTabsSticky}
      style={{ '--tabs-top': `${topPosition}px` } as React.CSSProperties}
      data-testid="section-tabs-container"
    >
      {filteredTabs.map((tab) => (
        <Tab
          key={tab.key}
          className={selectedTab === tab.key ? 'selected' : ''}
          onClick={() => handleTabClick(tab.key)}
          data-testid={tab.testId}
        >
          {tab.label}
        </Tab>
      ))}
    </TabsContainer>
  );
};

export default HorizontalTabss;
